import React, { useState, useEffect } from 'react'
import { ChildResponse } from '../types/child'

interface ChildDisplayProps {
  child: ChildResponse
  onAction?: (action: string) => void
}

export function ChildDisplay({ child, onAction }: ChildDisplayProps): React.ReactElement {
  const getEmoji = (mood: string) => {
    const moods: Record<string, string> = {
      happy: '😊',
      sad: '😢',
      angry: '😠',
      tired: '😴',
      excited: '🤩',
      neutral: '😐',
    }
    return moods[mood] || '😊'
  }

  const getStatColor = (value: number) => {
    if (value < 30) return 'text-red-600'
    if (value < 60) return 'text-yellow-600'
    return 'text-green-600'
  }

  return (
    <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg p-6 shadow-lg max-w-sm">
      {/* Avatar */}
      <div className="flex justify-center mb-4">
        <div
          dangerouslySetInnerHTML={{ __html: child.avatar || '' }}
          className="w-24 h-24"
        />
      </div>

      {/* Name and Mood */}
      <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">{child.name}</h2>
      <div className="text-center text-4xl mb-4">{getEmoji(child.mood)}</div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-white p-2 rounded text-sm">
          <span className="font-semibold">Age:</span>
          <p className="text-gray-700">{child.age} days</p>
        </div>
        <div className="bg-white p-2 rounded text-sm">
          <span className="font-semibold">Stage:</span>
          <p className="text-gray-700 capitalize">{child.stage}</p>
        </div>

        <div className="bg-white p-2 rounded text-sm">
          <span className="font-semibold">Health:</span>
          <div className="w-full bg-gray-300 rounded h-2 mt-1">
            <div
              className="bg-red-500 h-2 rounded"
              style={{ width: `${child.health}%` }}
            />
          </div>
        </div>

        <div className="bg-white p-2 rounded text-sm">
          <span className="font-semibold">Energy:</span>
          <div className="w-full bg-gray-300 rounded h-2 mt-1">
            <div
              className="bg-yellow-500 h-2 rounded"
              style={{ width: `${child.energyLevel}%` }}
            />
          </div>
        </div>

        <div className="bg-white p-2 rounded text-sm">
          <span className="font-semibold">Hunger:</span>
          <div className="w-full bg-gray-300 rounded h-2 mt-1">
            <div
              className="bg-orange-500 h-2 rounded"
              style={{ width: `${child.hungerLevel}%` }}
            />
          </div>
        </div>

        <div className="bg-white p-2 rounded text-sm">
          <span className="font-semibold">Happiness:</span>
          <div className="w-full bg-gray-300 rounded h-2 mt-1">
            <div
              className="bg-pink-500 h-2 rounded"
              style={{ width: `${child.happinessLevel}%` }}
            />
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-2 mb-6 text-center text-sm">
        <div className={`${getStatColor(child.intelligenceLevel)} font-semibold`}>
          Intelligence
          <p>{Math.round(child.intelligenceLevel)}</p>
        </div>
        <div className={`${getStatColor(child.creativityLevel)} font-semibold`}>
          Creativity
          <p>{Math.round(child.creativityLevel)}</p>
        </div>
        <div className={`${getStatColor(child.happinessLevel)} font-semibold`}>
          Happiness
          <p>{Math.round(child.happinessLevel)}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => onAction?.('feed')}
          className="bg-orange-500 text-white py-2 rounded font-semibold hover:bg-orange-600 text-sm"
        >
          🍎 Feed
        </button>
        <button
          onClick={() => onAction?.('play')}
          className="bg-pink-500 text-white py-2 rounded font-semibold hover:bg-pink-600 text-sm"
        >
          🎮 Play
        </button>
        <button
          onClick={() => onAction?.('study')}
          className="bg-blue-500 text-white py-2 rounded font-semibold hover:bg-blue-600 text-sm"
        >
          📚 Study
        </button>
        <button
          onClick={() => onAction?.('rest')}
          className="bg-purple-500 text-white py-2 rounded font-semibold hover:bg-purple-600 text-sm"
        >
          😴 Rest
        </button>
      </div>
    </div>
  )
}
